import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import Button from "./common/Button";
import { ArrowLeft, Download, Printer, Edit, Trash2 } from "lucide-react";
import './CustomConsentEditor.css';
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/AuthContext";
import customToast from "./common/Toaster";
import { formatDateDMY } from "../lib/ScheduleUtils";
import useBrandTranslation from "../hooks/useBrandTranslation";

export default function CustomConsentViewer() {
  const { t } = useBrandTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id && user) {
      loadDocument(parseInt(id));
    }
  }, [id, user, navigate]);

  const formatDate = (dateValue) =>
    dateValue ? formatDateDMY(dateValue) : t("app.clinical.dateUnavailable", "Fecha no disponible");

  const loadDocument = async (documentId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("custom_consent_documents")
        .select("*")
        .eq("id", documentId)
        .eq("created_by", user.id)
        .eq("is_active", true)
        .single();

      if (error) {
        console.error("Error loading document:", error);
        customToast(t("app.consent.loadError", "Error al cargar el documento"), "danger");
        navigate("/informed-consent");
        return;
      }

      if (data) {
        setDocument(data);
      } else {
        customToast(t("app.consent.docNotFound", "Documento no encontrado"), "danger");
        navigate("/informed-consent");
      }
    } catch (error) {
      console.error("Error loading document:", error);
      customToast(t("app.consent.loadError", "Error al cargar el documento"), "danger");
      navigate("/informed-consent");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // Crear ventana de impresión
    const formattedDate = formatDate(document.created_at || document.createdAt);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${document.title}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 2px solid #333; 
              padding-bottom: 20px;
            }
            .title { 
              font-size: 24px; 
              font-weight: bold; 
              margin-bottom: 10px;
            }
            .subtitle { 
              font-size: 16px; 
              color: #666; 
              margin-bottom: 20px;
            }
            .content { 
              white-space: pre-wrap; 
              font-size: 14px;
            }
            .footer { 
              margin-top: 40px; 
              text-align: center; 
              font-size: 12px; 
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${document.title}</div>
            <div class="subtitle">${t("app.clinical.informedConsentSubtitle", "Consentimiento Informado")}</div>
          </div>
          <div class="content">${document.content}</div>
          <div class="footer">
            <p>{t("app.consent.createdOn", "Documento creado el:")} ${formattedDate}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleDownloadPDF = () => {
    // Crear ventana limpia solo con título y contenido para PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${document.title}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 40px; 
              line-height: 1.6;
            }
            .title { 
              font-size: 28px; 
              font-weight: bold; 
              margin-bottom: 30px;
              text-align: center;
            }
            .content { 
              font-size: 14px;
              line-height: 1.8;
            }
            @media print {
              body { margin: 20px; }
            }
            @page {
              margin: 20mm;
            }
          </style>
        </head>
        <body>
          <div class="title">${document.title}</div>
          <div class="content">${document.content}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    // Pequeño delay para asegurar que el contenido se carga antes de imprimir
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  };

  const handleEdit = () => {
    navigate(`/informed-consent/edit/${id}`);
  };

  const handleDelete = async () => {
    if (confirm(t("app.consent.confirmDelete", "¿Estás seguro de que quieres eliminar este documento?"))) {
      try {
        const { error } = await supabase
          .from("custom_consent_documents")
          .update({ is_active: false })
          .eq("id", parseInt(id))
          .eq("created_by", user.id);

        if (error) {
          console.error("Error deleting document:", error);
          customToast(t("app.consent.deleteError", "Error al eliminar el documento"), "danger");
          return;
        }

        customToast(t("app.consent.deleteSuccess", "Documento eliminado exitosamente"), "success");
        navigate("/informed-consent");
      } catch (error) {
        console.error("Error deleting document:", error);
        customToast(t("app.consent.deleteError", "Error al eliminar el documento"), "danger");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{t("app.consent.loading", "Cargando documento...")}</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t("app.consent.docNotFound", "Documento no encontrado")}</h1>
          <Button onClick={() => navigate("/informed-consent")}>
            {t("app.consent.backToDocs", "Volver a Documentos")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate("/informed-consent")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("app.consent.backToDocs", "Volver a Documentos")}
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {document.title}
          </h1>
          <p className="text-gray-600">
            {t("app.consent.customDocSubtitle", "Documento de consentimiento personalizado")}
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>{t("app.consent.viewTitle", "Vista del Documento")}</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleEdit}
                  className="flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  {t("app.consent.edit", "Editar")}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                  {t("app.consent.delete", "Eliminar")}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-white border rounded-lg p-6 min-h-[500px]">
              <div className="prose max-w-none">
                <h2 className="text-2xl font-bold mb-6 text-center border-b pb-4">
                  {document.title}
                </h2>
                <div className="text-center mb-6">
                  <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {t("app.clinical.informedConsentSubtitle", "Consentimiento Informado")}
                  </span>
                </div>
                <div 
                  className="preview-content text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: document.content }}
                />
                <div className="mt-8 pt-4 border-t text-center text-xs text-gray-500">
                  <p>{t("app.consent.createdOn", "Documento creado el:")} {formatDate(document.created_at || document.createdAt)}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handlePrint} className="flex items-center gap-2">
                <Printer className="w-4 h-4" />
                {t("app.consent.printDocument", "Imprimir Documento")}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleDownloadPDF}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {t("app.consent.downloadPdf", "Descargar PDF")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
